import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { ArrowRight, Calendar, MapPin, Users, MessageSquare, Star, ChevronDown, Bot } from 'lucide-react';
import { useVenueStore } from '../store/useVenueStore';

export const TeamBuildingActivitiesPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { setShowAIAssistant } = useVenueStore();

  const openAIAssistant = () => {
    setShowAIAssistant(true);
  };

  const teamBuildingActivities = [
    {
      id: 1,
      title: "Iskonferens och isskulptering på ICEHOTEL",
      location: "Jukkasjärvi, Sverige",
      image: "/assets/images/icehotel-teambuilding.webp",
      description: "Håll möten i en miljö av is och snö, och delta i isskulpturworkshops för teambuilding. En helt unik upplevelse där kreativitet möter extrem natur.",
      highlights: [
        "Skapa konstverk av is under ledning av professionella konstnärer",
        "Exklusiva konferenslokaler byggda helt av is och snö",
        "Övernattning i isrum som en oförglömlig gruppupplevelse",
        "Kombination av lyx och vildmark i arktisk miljö"
      ],
      rating: 4.9,
      reviewCount: 127,
      priceRange: "Högt",
      difficulty: "Medel",
      duration: "2-3 dagar",
      capacity: "10-60 personer",
      bestSeason: "December-April"
    },
    {
      id: 2,
      title: "Trädtoppsäventyr och zipline",
      location: "Småland, Sverige",
      image: "/assets/images/zipline-smaland.webp",
      description: "Utmana teamet med höghöjdsbanor och ziplines i Smålands vackra skogar. En perfekt kombination av fysisk aktivitet, samarbete och vacker natur.",
      highlights: [
        "Över 12 olika banor med varierande svårighetsgrad",
        "Sveriges längsta ziplines genom orörd natur",
        "Teamövningar som kräver samarbete och tillit",
        "Professionella instruktörer som anpassar dagen efter er grupp"
      ],
      rating: 4.8,
      reviewCount: 215,
      priceRange: "Medel",
      difficulty: "Medel-Hög",
      duration: "Halvdag-Heldag",
      capacity: "8-50 personer",
      bestSeason: "Maj-Oktober"
    },
    {
      id: 3,
      title: "Virtuell verklighetsupplevelse",
      location: "Tekniska museet, Stockholm, Sverige",
      image: "/assets/images/vr-experience.webp",
      description: "Kombinera konferens med en VR-upplevelse som stimulerar kreativitet och samarbete. Upplev framtidens teknologi i en historisk miljö.",
      highlights: [
        "Specialdesignade VR-spel för teambuilding och samarbete",
        "Konferenslokaler i direkt anslutning till VR-centret",
        "Möjlighet att skapa virtuella produktprototyper",
        "Unika insikter om hur team kommunicerar i virtuella miljöer"
      ],
      rating: 4.7,
      reviewCount: 93,
      priceRange: "Medel",
      difficulty: "Låg",
      duration: "Halvdag",
      capacity: "10-30 personer",
      bestSeason: "Året runt"
    },
    {
      id: 4,
      title: "Escape Room-utmaning",
      location: "Mystery Makers, Köpenhamn, Danmark",
      image: "/assets/images/escape-room.webp",
      description: "Stärk teamkänslan genom att lösa gåtor och mysterier tillsammans i en spännande miljö. Tidspressen skapar fokus och samarbete.",
      highlights: [
        "Specialdesignade rum för företagsgrupper med affärsrelaterade teman",
        "Utmaningar som kräver olika kompetenser - perfekt för att upptäcka dolda talanger",
        "Detaljerad feedback om gruppens samarbete efter aktiviteten",
        "Möjlighet att kombinera med konferenslokaler i samma byggnad"
      ],
      rating: 4.8,
      reviewCount: 187,
      priceRange: "Låg-Medel",
      difficulty: "Medel",
      duration: "1-2 timmar",
      capacity: "6-60 personer (uppdelat i mindre grupper)",
      bestSeason: "Året runt"
    },
    {
      id: 5,
      title: "Bygg en vikingabåt",
      location: "Vikingaskeppsmuseet, Roskilde, Danmark",
      image: "/assets/images/viking-boat.webp",
      description: "Arbeta tillsammans för att bygga en traditionell vikingabåt och lär er om nordisk historia. En perfekt kombination av praktiskt hantverk och kulturell upplevelse.",
      highlights: [
        "Handledning av experter i traditionellt vikingaskeppsbygge",
        "Insikt i ledarskap och projektorganisering genom historiskt perspektiv",
        "Testa färdigställda båtar på Roskildefjorden",
        "Historisk måltid i vikingastil som avslutning"
      ],
      rating: 4.9,
      reviewCount: 74,
      priceRange: "Hög",
      difficulty: "Medel",
      duration: "1-2 dagar",
      capacity: "10-30 personer",
      bestSeason: "April-Oktober"
    },
    {
      id: 6,
      title: "Gourmet dressing Björnstorp",
      location: "Björnstorp, Sverige",
      image: "/assets/images/bjornstorp-rail.webp",
      description: "Njut av en fantastisk somrig cykeltur på räls längs Romeleåsens dalgång med tre stopp för en utsökt meny med lokala delikatesser från skånska producenter och bönder.",
      highlights: [
        "Tre timmars upplevelse med cykling på räls genom vacker natur",
        "Tre stopp med guidning och olika utsökta rätter från lokala producenter",
        "Hållbar turism med fokus på lokala produkter och miljömedvetenhet",
        "Cyklar för två personer där ni kan turas om att styra"
      ],
      rating: 4.8,
      reviewCount: 136,
      priceRange: "Medel",
      difficulty: "Låg",
      duration: "3 timmar",
      capacity: "10-30 personer",
      bestSeason: "Maj-September",
      details: {
        timeRequired: "3 timmar",
        ageLimit: "12+",
        priceFrom: "1295kr",
        included: [
          "Cykling på räls",
          "Guide",
          "Måltider med lokala delikatesser"
        ],
        excluded: [
          "Alkohol (men kan tas med själv)"
        ]
      }
    },
    {
      id: 7,
      title: "Samisk upplevelse med rensläde",
      location: "Tromsø, Norge",
      image: "/assets/images/sami-experience.webp",
      description: "Lär känna den samiska kulturen genom renslädsturer och berättelser från renskötare. En autentisk kulturupplevelse i spektakulär natur.",
      highlights: [
        "Renskötare delar traditioner som gått i generationer",
        "Slädturer genom snötäckta landskap",
        "Samisk matlagning och storytelling i en traditionell lavvu",
        "Möjlighet att se norrsken under vinterhalvåret"
      ],
      rating: 4.9,
      reviewCount: 62,
      priceRange: "Hög",
      difficulty: "Låg-Medel",
      duration: "1-2 dagar",
      capacity: "10-25 personer",
      bestSeason: "November-April"
    },
    // Remaining activities would be included here
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header 
        onMenuClick={() => {}} 
        onAboutClick={() => {}} 
        onContactClick={() => {}}
      />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 bg-gradient-to-b from-[#2d2f4e] to-[#3d4070] text-white overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-20 right-20 w-64 h-64 rounded-full bg-[#fabddf]/10 blur-xl"></div>
        <div className="absolute bottom-10 left-10 w-40 h-40 rounded-full bg-[#fabddf]/10 blur-lg"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              15 Inspirerande Teambuilding-aktiviteter i Skandinavien
            </h1>
            <p className="text-xl text-gray-200 mb-10 leading-relaxed">
              Upptäck unika upplevelser som stärker samarbetet, bygger laganda och skapar minnen för livet
            </p>
            <button
              onClick={openAIAssistant}
              className="inline-flex items-center justify-center bg-[#fabddf] hover:bg-[#ffd3ee] text-[#2d2f4e] font-semibold rounded-full px-8 py-4 transition-all hover:scale-105 text-lg"
            >
              <Bot className="mr-2 h-5 w-5" />
              <span>Få personliga förslag med vår AI-assistent</span>
            </button>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-10 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center">
            <div className="w-full md:w-1/3 p-4 text-center">
              <p className="text-4xl font-bold text-[#2d2f4e]">15+</p>
              <p className="text-gray-600">Unika aktiviteter</p>
            </div>
            <div className="w-full md:w-1/3 p-4 text-center">
              <p className="text-4xl font-bold text-[#2d2f4e]">100%</p>
              <p className="text-gray-600">Kvalitetssäkrade</p>
            </div>
            <div className="w-full md:w-1/3 p-4 text-center">
              <p className="text-4xl font-bold text-[#2d2f4e]">4.8/5</p>
              <p className="text-gray-600">Kundbetyg</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why Teambuilding Matters */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold text-[#2d2f4e] mb-6">Varför investera i teambuilding?</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Effektiva teambuilding-aktiviteter stärker arbetsplatsrelationer, förbättrar kommunikation och ökar produktivitet. Våra handplockade aktiviteter är utformade för att skapa bestående värde för ert team.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-16 h-16 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-[#2d2f4e]" />
              </div>
              <h3 className="text-xl font-bold text-[#2d2f4e] mb-4">Stärker teamkänslan</h3>
              <p className="text-gray-600">
                Gemensamma upplevelser utanför kontoret skapar starkare band mellan kollegor och bygger tillit inom gruppen.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-16 h-16 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-6">
                <MessageSquare className="w-8 h-8 text-[#2d2f4e]" />
              </div>
              <h3 className="text-xl font-bold text-[#2d2f4e] mb-4">Förbättrar kommunikation</h3>
              <p className="text-gray-600">
                I nya miljöer med roliga utmaningar upptäcker ni nya sätt att kommunicera och lösa problem tillsammans.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-16 h-16 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-6">
                <Star className="w-8 h-8 text-[#2d2f4e]" />
              </div>
              <h3 className="text-xl font-bold text-[#2d2f4e] mb-4">Ökar motivation</h3>
              <p className="text-gray-600">
                Välplanerade aktiviteter inspirerar, energiserar och ger teamet en känsla av uppskattning som består länge efteråt.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* First set of activities */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#2d2f4e] mb-16 text-center">Våra topprankade teambuilding-aktiviteter</h2>
          
          {teamBuildingActivities.slice(0, 5).map((activity, index) => (
            <div key={activity.id} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 mb-20`}>
              <div className="lg:w-1/2 relative">
                <div className="relative rounded-2xl overflow-hidden">
                  <img 
                    src={activity.image}
                    alt={activity.title}
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-[#2d2f4e] text-white text-2xl font-bold w-12 h-12 rounded-full flex items-center justify-center">
                    {activity.id}
                  </div>
                </div>
                
                <div className="absolute -bottom-4 right-4 bg-white rounded-lg shadow-lg p-4 flex items-center">
                  <Star className="text-yellow-400 w-5 h-5 mr-1" fill="currentColor" />
                  <span className="font-bold text-[#2d2f4e]">{activity.rating}</span>
                  <span className="text-gray-500 ml-1">({activity.reviewCount} omdömen)</span>
                </div>
              </div>
              
              <div className="lg:w-1/2">
                <div className="flex items-center mb-4">
                  <MapPin className="w-5 h-5 text-[#fabddf] mr-2" />
                  <span className="text-gray-600">{activity.location}</span>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                    {activity.duration}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                    {activity.capacity}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                    Svårighetsgrad: {activity.difficulty}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                    Bästa säsong: {activity.bestSeason}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                    Prisklass: {activity.priceRange}
                  </span>
                </div>
                
                <h3 className="text-3xl font-bold text-[#2d2f4e] mb-4">{activity.title}</h3>
                <p className="text-gray-600 mb-6 text-lg">{activity.description}</p>
                
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-[#2d2f4e] mb-3">Höjdpunkter:</h4>
                  <ul className="space-y-2">
                    {activity.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#fabddf] mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <button 
                  onClick={openAIAssistant}
                  className="inline-flex items-center justify-center bg-[#2d2f4e] hover:bg-[#3d4070] text-white font-semibold rounded-full px-6 py-3 transition-all hover:scale-105"
                >
                  Få mer information om denna aktivitet
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
      
      {/* AI Assistant Feature Section */}
      <section className="py-16 bg-gradient-to-r from-[#2d2f4e] to-[#3d4070] text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center">
            <div className="lg:w-1/2 mb-10 lg:mb-0">
              <h2 className="text-3xl font-bold mb-6">Låt vår AI-assistent hjälpa dig hitta rätt aktivitet</h2>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Osäker på vilken teambuilding-aktivitet som passar just ert team bäst? Vår AI-assistent hjälper dig baserat på era specifika behov och önskemål.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <div className="bg-[#fabddf]/20 rounded-full p-1 mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#fabddf]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Personliga rekommendationer baserade på era mål</span>
                </li>
                <li className="flex items-start">
                  <div className="bg-[#fabddf]/20 rounded-full p-1 mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#fabddf]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Anpassade förslag för gruppstorlek och budget</span>
                </li>
                <li className="flex items-start">
                  <div className="bg-[#fabddf]/20 rounded-full p-1 mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#fabddf]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span>Detaljerad information om de bästa alternativen</span>
                </li>
              </ul>
              <button 
                onClick={openAIAssistant} 
                className="inline-flex items-center justify-center bg-[#fabddf] hover:bg-[#ffd3ee] text-[#2d2f4e] font-semibold rounded-full px-8 py-4 transition-all hover:scale-105 text-lg"
              >
                <Bot className="mr-2 h-5 w-5" />
                <span>Starta AI-assistenten</span>
              </button>
            </div>
            
            <div className="lg:w-1/2 lg:pl-16">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#fabddf] flex items-center justify-center mr-3">
                    <Bot className="h-6 w-6 text-[#2d2f4e]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">TeamBuilding AI-assistent</h3>
                    <p className="text-sm text-gray-300">Intelligent hjälp för ert nästa event</p>
                  </div>
                </div>
                
                <div className="space-y-4 mb-4">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-sm italic">
                      "Hej! Jag hjälper er att hitta den perfekta teambuilding-aktiviteten. Berätta om ert team, önskad aktivitetsnivå och andra preferenser så kan jag ge personliga förslag."
                    </p>
                  </div>
                  
                  <div className="bg-[#fabddf]/10 rounded-lg p-3 ml-6">
                    <p className="text-sm italic">
                      "Vi är ett team på 12 personer från IT-avdelningen. Vi vill gärna göra något utomhus i Stockholm-området. Medelhög aktivitetsnivå. Budget runt 1500kr per person."
                    </p>
                  </div>
                  
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-sm italic">
                      "Baserat på era önskemål skulle jag rekommendera ett trädtoppsäventyr i Stockholms skärgård eller ett utomhus Escape Game som 'Troll Escape'. Båda aktiviteterna passar er gruppstorlek, budget och önskemål om att vara utomhus med måttlig aktivitetsnivå."
                    </p>
                  </div>
                </div>
                
                <div className="border-t border-white/10 pt-4">
                  <div className="rounded-full bg-white/10 flex items-center p-2">
                    <input 
                      type="text" 
                      className="bg-transparent text-white outline-none flex-1 px-2" 
                      placeholder="Ställ en fråga..." 
                      disabled
                    />
                    <button 
                      className="bg-[#fabddf] hover:bg-[#ffd3ee] text-[#2d2f4e] rounded-full p-2" 
                      onClick={openAIAssistant}
                    >
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Planning Tips Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#2d2f4e] mb-10 text-center">Tips för att planera en lyckad teambuilding</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#2d2f4e] font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Definiera tydliga mål</h3>
              <p className="text-gray-600">
                Bestäm vad ni vill uppnå med aktiviteten. Handlar det om att förbättra kommunikation, bygga tillit, eller helt enkelt ha roligt tillsammans? Tydliga mål gör det lättare att välja rätt aktivitet.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#2d2f4e] font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Inkludera alla</h3>
              <p className="text-gray-600">
                Se till att aktiviteten är tillgänglig för alla teammedlemmar. Ta hänsyn till olika fysiska förutsättningar, intressen och bekvämlighetsområden för att ingen ska känna sig exkluderad.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#2d2f4e] font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Planera i god tid</h3>
              <p className="text-gray-600">
                Populära aktiviteter och platser bokas ofta upp snabbt, särskilt under högsäsong. Börja planeringen minst 2-3 månader i förväg för bästa möjligheter och priser.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#2d2f4e] font-bold text-xl">4</span>
              </div>
              <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Kombinera med reflektion</h3>
              <p className="text-gray-600">
                Avsätt tid efter aktiviteten för att reflektera kring upplevelsen. Vad lärde ni er? Hur kan insikterna appliceras i det dagliga arbetet? Detta förstärker effekten av teambuildingen.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#2d2f4e] font-bold text-xl">5</span>
              </div>
              <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Balansera utmaning och nöje</h3>
              <p className="text-gray-600">
                Den bästa teambuildingen är både utmanande och rolig. För utmanande aktiviteter kan skapa stress, medan för enkla kanske inte engagerar tillräckligt. Hitta rätt balans för ert team.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="w-12 h-12 bg-[#fabddf]/20 rounded-full flex items-center justify-center mb-4">
                <span className="text-[#2d2f4e] font-bold text-xl">6</span>
              </div>
              <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Dokumentera upplevelsen</h3>
              <p className="text-gray-600">
                Ta foton och videos under aktiviteten för att dela internt efteråt. Det förlänger den positiva effekten och skapar gemensamma minnen att återvända till.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2d2f4e] to-[#3d4070] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Redo att skapa oförglömliga teamupplevelser?</h2>
            <p className="text-xl text-gray-200 mb-10 leading-relaxed">
              Låt oss hjälpa dig att hitta den perfekta teambuilding-aktiviteten som passar just era behov, budget och mål.
            </p>
            <button
              onClick={openAIAssistant}
              className="inline-flex items-center justify-center bg-[#fabddf] hover:bg-[#ffd3ee] text-[#2d2f4e] font-semibold rounded-full px-8 py-4 transition-all hover:scale-105 text-lg"
            >
              <Bot className="mr-2 h-5 w-5" />
              <span>Få personliga förslag med vår AI-assistent</span>
            </button>
          </div>
        </div>
      </section>
      
      {/* Related Content Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#2d2f4e] mb-10 text-center">Relaterat innehåll</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <img 
                src="/assets/images/konferens-guide.webp" 
                alt="Guide till att välja rätt konferenslokal" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Guide: Välj rätt konferenslokal</h3>
                <p className="text-gray-600 mb-4">Hur du hittar den perfekta lokalen för ert nästa företagsevent eller konferens.</p>
                <Link 
                  to="/" 
                  className="inline-flex items-center text-[#fabddf] hover:text-[#2d2f4e] transition-colors"
                >
                  Läs mer
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
            
            <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <img 
                src="/assets/images/hybrid-meetings.webp" 
                alt="Hybridmöten - Framtidens konferenser" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Hybridmöten - Framtidens konferenser</h3>
                <p className="text-gray-600 mb-4">Hur du kombinerar fysiska och virtuella mötesformat för maximalt engagemang.</p>
                <Link 
                  to="/" 
                  className="inline-flex items-center text-[#fabddf] hover:text-[#2d2f4e] transition-colors"
                >
                  Läs mer
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
            
            <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <img 
                src="/assets/images/sustainable-events.webp" 
                alt="Hållbara företagsevent" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-[#2d2f4e] mb-3">Hållbara företagsevent</h3>
                <p className="text-gray-600 mb-4">Tips och råd för att arrangera miljövänliga och socialt hållbara konferenser och aktiviteter.</p>
                <Link 
                  to="/" 
                  className="inline-flex items-center text-[#fabddf] hover:text-[#2d2f4e] transition-colors"
                >
                  Läs mer
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-[#2d2f4e] text-white py-10 border-t border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center">
            <div className="text-center">
              <div className="flex items-center justify-center mb-6">
                <svg width="40" height="40" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-3">
                  <circle cx="100" cy="100" r="100" fill="#FABDDF" fillOpacity="0.2"/>
                  <circle cx="100" cy="100" r="40" fill="#FABDDF" />
                </svg>
                <span className="text-xl font-bold">Scandinavian Conference</span>
              </div>
              
              <div className="text-sm text-gray-300">
                © {new Date().getFullYear()} Scandinavian Conference. Alla rättigheter förbehållna.
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}; 